import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  TextInput,
  Text,
  StyleSheet,
  Animated,
  TouchableWithoutFeedback,
  Platform,
} from 'react-native';
import { Color, CommonProperties } from '../styles';
import { scale } from '../utils/scale.utils';

const OutlinedTextInput = ({
  label,
  value,
  onChangeText,
  error,
  style,
  secureTextEntry,
  keyboardType,
  autoCapitalize = 'none',
  placeholder,
  multiline = false,
  numberOfLines = 1,
  ...props
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const animatedLabelPosition = useRef(new Animated.Value(value ? 1 : 0)).current;
  const inputRef = useRef(null);

  useEffect(() => {
    Animated.timing(animatedLabelPosition, {
      toValue: isFocused || value ? 1 : 0,
      duration: 150,
      useNativeDriver: false,
    }).start();
  }, [isFocused, value, animatedLabelPosition]);

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleBlur = () => {
    setIsFocused(false);
  };

  const handlePress = () => {
    if (inputRef.current && !isFocused) {
      inputRef.current.focus();
    }
  };

  // Determine colors based on state
  const labelColor = error ? Color.required : isFocused ? Color.primary : Color.textSecondary;

  const borderColor = error ? Color.required : isFocused ? Color.primary : Color.gray;

  const labelBackgroundColor = isFocused || value ? 'white' : 'transparent';

  // Animated properties for the label
  const labelStyle = {
    position: 'absolute',
    left: 16,
    top: animatedLabelPosition.interpolate({
      inputRange: [0, 1],
      outputRange: [multiline ? 16 : 17, -8],
    }),
    fontSize: animatedLabelPosition.interpolate({
      inputRange: [0, 1],
      outputRange: [16, 12],
    }),
    color: animatedLabelPosition.interpolate({
      inputRange: [0, 1],
      outputRange: [Color.textSecondary, labelColor],
    }),
    backgroundColor: labelBackgroundColor,
    paddingHorizontal: animatedLabelPosition.interpolate({
      inputRange: [0, 1],
      outputRange: [0, 4],
    }),
    zIndex: 1,
  };

  const inputContainerStyle = [
    styles.inputContainer,
    {
      borderColor,
      borderWidth: isFocused ? 2 : 1,
      minHeight: multiline ? 80 : 56,
    },
    multiline && styles.multilineContainer,
  ];

  return (
    <View style={[styles.container, style]}>
      <TouchableWithoutFeedback onPress={handlePress}>
        <View style={inputContainerStyle}>
          <Animated.Text style={labelStyle}>{label}</Animated.Text>
          <TextInput
            ref={inputRef}
            style={[
              styles.input,
              multiline && styles.multilineInput,
              isFocused && styles.focusedInput,
            ]}
            value={value}
            onChangeText={onChangeText}
            onFocus={handleFocus}
            onBlur={handleBlur}
            secureTextEntry={secureTextEntry}
            keyboardType={keyboardType}
            autoCapitalize={autoCapitalize}
            placeholder={isFocused ? placeholder : ''}
            placeholderTextColor={Color.textSecondary}
            multiline={multiline}
            numberOfLines={multiline ? numberOfLines : 1}
            textAlignVertical={multiline ? 'top' : 'center'}
            underlineColorAndroid="transparent"
            selectionColor={Color.primary}
            {...props}
          />
        </View>
      </TouchableWithoutFeedback>
      {error && typeof error === 'string' && <Text style={styles.errorText}>{error}</Text>}
      {error && typeof error !== 'string' && error && (
        <Text style={styles.errorText}>This field is required</Text>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    marginVertical: 8,
    width: '100%',
  },
  inputContainer: {
    borderWidth: 1,
    borderRadius: 8,
    minHeight: 56,
    paddingHorizontal: 12,
    justifyContent: 'center',
    backgroundColor: Color.accent,
    ...Platform.select({
      ios: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 0.1,
        shadowRadius: 1,
      },
      android: {
        elevation: 1,
      },
    }),
  },
  multilineContainer: {
    paddingTop: 8,
    alignItems: 'flex-start',
  },
  input: {
    fontSize: 16,
    color: Color.textPrimary,
    paddingVertical: 8,
    paddingTop: 16,
    paddingBottom: 8,
    width: '100%',
    outlineStyle: 'none', // Removes outline on web
  },
  multilineInput: {
    paddingTop: 16,
    height: 'auto',
  },
  focusedInput: {
    color: Color.textPrimary,
  },
  errorText: {
    color: Color.required,
    fontSize: 12,
    marginTop: 4,
    marginLeft: 12,
    ...CommonProperties.body,
  },
});

export default OutlinedTextInput;
